"use client";

import { useMemo } from "react";

import { useStatsigConfig } from "@/contexts/statsig-config-context";
import { env } from "@/env";

// Re-export types so consumers don't need to change imports
export type {
  BannerSeverity,
  GlobalAnnouncementConfig,
  MarketStatusOverride,
} from "@/lib/statsig-configs";

/**
 * Hook to check if maintenance mode is enabled.
 * Reads server-evaluated Statsig config from context.
 */
export function useMaintenanceMode(): {
  isMaintenanceMode: boolean;
  message?: string;
} {
  const { maintenanceMode } = useStatsigConfig();
  const envMaintenanceMode = env.NEXT_PUBLIC_MAINTENANCE_MODE;

  return useMemo(() => {
    if (envMaintenanceMode) {
      return { isMaintenanceMode: true, message: undefined };
    }
    return maintenanceMode;
  }, [envMaintenanceMode, maintenanceMode]);
}

/**
 * Hook to get global announcement configuration.
 * Reads server-evaluated Statsig config from context.
 */
export function useGlobalAnnouncement() {
  const { globalAnnouncement } = useStatsigConfig();
  return globalAnnouncement;
}

/**
 * Hook to get market-specific status override.
 * Reads server-evaluated Statsig config from context.
 */
export function useMarketStatusOverride(marketName: string) {
  const { marketStatusOverrides } = useStatsigConfig();

  return useMemo(() => {
    const override = marketStatusOverrides[marketName];
    if (!override || !override.enabled || !override.message) {
      return null;
    }
    return override;
  }, [marketStatusOverrides, marketName]);
}
